/**
 * Decides what to do after signup completes for users who came in through
 * a scoped invite link (task / project / team). Pure function so it can be
 * tested without mounting the navigator or stubbing sessionStorage.
 *
 * The corresponding effect in app-stack.web.tsx reads sessionStorage,
 * calls this, and dispatches on action.kind. **All sessionStorage key
 * clearing is driven by the action** — the effect never hard-codes a
 * key list. Each action carries `keysToClearBefore` and
 * `keysToClearAfter` arrays so the effect can preserve the original
 * pre-navigate / post-navigate split.
 *
 * Pinned by PR #4967 (TA-9524 — task post-signup navigation).
 */

import { SCOPED_INVITE_TYPES } from './scopedInviteTypes';

export type PostSignupSession = {
  inviteSignup: string | null;
  scopedTokenType: string | null;
  /** sessionStorage `task_or_project_id` — task numeric id OR project/team UUID */
  taskOrProjectId: string | null;
  isLinkInvite: boolean;
};

/**
 * Common shape: every post-signup action tells the effect exactly which
 * sessionStorage keys to clear and when. The effect must call
 * `clearKeys(keysToClearBefore)` *before* the side effect (navigate or
 * createUserRequest) and `clearKeys(keysToClearAfter)` *after* it.
 */
type WithKeyClearing = {
  keysToClearBefore: string[];
  keysToClearAfter: string[];
};

export type PostSignupAction =
  /** Task link-invite (join request submitted, pending) → task-welcome */
  | (WithKeyClearing & { kind: 'task-link-welcome'; taskId: string })
  /** Task email-invite — handled by a separate pending_project_uuid effect */
  | (WithKeyClearing & { kind: 'task-email-noop' })
  /** Project/Task scoped invite where the id is a UUID → projects-stack/details */
  | (WithKeyClearing & { kind: 'project-scoped'; projectUUId: string })
  /** Team link-invite → createUserRequest + navigate to team detail */
  | (WithKeyClearing & { kind: 'team-link-invite'; teamUUId: string })
  /** No post-signup navigation needed */
  | { kind: 'none' };

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Exported for teamLinkLoginNavigation.ts so both share one definition. */
export const isValidUuid = (value: string | null): value is string =>
  !!value && UUID_RE.test(value);

// Task branches clear all keys before the navigate (or no-op).
const TASK_INVITE_KEYS = [
  'invite_signup',
  'is_link_invite',
  'link_type',
  'task_or_project_id',
  'invite_signup_type',
  'invitation_token',
  'invite_task_project_uuid',
];

// Project-scoped: clear the "should we run?" flags before navigate, the
// data keys after — preserves the original ordering from app-stack.web.tsx.
const PROJECT_SCOPED_KEYS_BEFORE = ['invite_signup', 'is_link_invite'];
const PROJECT_SCOPED_KEYS_AFTER = [
  'link_type',
  'task_or_project_id',
  'invite_signup_type',
  'invitation_token',
];

// Team link-invite: same pre/post split as project-scoped, more keys to clear.
const TEAM_LINK_KEYS_BEFORE = ['invite_signup', 'is_link_invite'];
const TEAM_LINK_KEYS_AFTER = [
  'link_type',
  'task_or_project_id',
  'team_preview_uuid',
  'invite_signup_type',
  'invitation_token',
  'team_invite_auto_join',
];

export const decidePostSignupNavigation = (
  s: PostSignupSession
): PostSignupAction => {
  const isInviteSignup = s.inviteSignup === '1';
  if (!isInviteSignup) return { kind: 'none' };

  if (s.scopedTokenType === 'task') {
    if (s.isLinkInvite && s.taskOrProjectId) {
      return {
        kind: 'task-link-welcome',
        taskId: s.taskOrProjectId,
        keysToClearBefore: TASK_INVITE_KEYS,
        keysToClearAfter: [],
      };
    }
    return {
      kind: 'task-email-noop',
      keysToClearBefore: TASK_INVITE_KEYS,
      keysToClearAfter: [],
    };
  }

  if (
    SCOPED_INVITE_TYPES.includes(s.scopedTokenType ?? '') &&
    isValidUuid(s.taskOrProjectId)
  ) {
    return {
      kind: 'project-scoped',
      projectUUId: s.taskOrProjectId,
      keysToClearBefore: PROJECT_SCOPED_KEYS_BEFORE,
      keysToClearAfter: PROJECT_SCOPED_KEYS_AFTER,
    };
  }

  if (
    s.scopedTokenType === 'team' &&
    s.isLinkInvite &&
    isValidUuid(s.taskOrProjectId)
  ) {
    return {
      kind: 'team-link-invite',
      teamUUId: s.taskOrProjectId,
      keysToClearBefore: TEAM_LINK_KEYS_BEFORE,
      keysToClearAfter: TEAM_LINK_KEYS_AFTER,
    };
  }

  return { kind: 'none' };
};
